import { useEffect, useState } from "react";
import axios from "axios";
import {
    FaUser,
    FaHotel,
    FaCalendarAlt,
    FaEnvelope,
    FaPhone,
    FaGlobe,
    FaCheckCircle,
    FaTimesCircle,
    FaStar,
    FaEdit,
    FaTrash,
    FaArrowRight,
    FaClock,
    FaLock,
    FaUserCircle,
} from "react-icons/fa";

export default function Profile({ onLoginClick }) {
    const [user, setUser] = useState(null);
    const [form, setForm] = useState({});
    const [bookings, setBookings] = useState([]);
    const [activeTab, setActiveTab] = useState("profile");
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);

    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [loading, setLoading] = useState(false);
    const [now, setNow] = useState(new Date());
    const [cancelLoading, setCancelLoading] = useState(null);

    /* =========================================================
       LIVE CLOCK
    ========================================================= */

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(new Date());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    /* =========================================================
       LOAD USER
    ========================================================= */

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem("user"));

        if (!storedUser) {
            setUser(null);
            setBookings([]);
            return;
        }

        setUser(storedUser);

        setForm({
            name: storedUser.name || "",
            email: storedUser.email || "",
            phone: storedUser.phone || "",
            nationality: storedUser.nationality || "",
        });

        fetchBookings(storedUser.id);
    }, []);

    /* =========================================================
       FETCH BOOKINGS
    ========================================================= */

    const fetchBookings = async (userId) => {
        try {
            const res = await axios.get(
                `https://dubaihotelrates.com/api/user/${userId}/bookings`
            );

            setBookings(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    /* =========================================================
       FORM
    ========================================================= */

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });
    };

    const updateProfile = async (e) => {
        e.preventDefault();

        try {
            const res = await axios.put(
                `https://dubaihotelrates.com/api/user/${user.id}`,
                form
            );

            localStorage.setItem(
                "user",
                JSON.stringify(res.data.user)
            );

            setUser(res.data.user);

            alert("Profile updated successfully");
        } catch (err) {
            console.error(err);
            alert("Update failed");
        }
    };

    /* =========================================================
       DATE HELPERS
    ========================================================= */

    const formatDate = (date) =>
        new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });

    const normalizeDate = (date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const isPastCheckout = (date) => {
        return normalizeDate(date) < normalizeDate(new Date());
    };

    const isFutureCheckIn = (date) => {
        return normalizeDate(date) > normalizeDate(new Date());
    };

    /* =========================================================
       CANCEL BOOKING
    ========================================================= */

    const getCancelDeadline = (booking) => {
        const createdAt = new Date(booking.created_at);
        const checkIn = new Date(booking.check_in);

        const freeCancelUntil = new Date(
            createdAt.getTime() + 24 * 60 * 60 * 1000
        );

        return freeCancelUntil < checkIn
            ? freeCancelUntil
            : checkIn;
    };

    const canCancel = (booking) => {
        return new Date() < getCancelDeadline(booking);
    };

    const getCancelCountdown = (booking) => {
        const currentTime = new Date();
        const deadline = getCancelDeadline(booking);

        const diff = deadline - currentTime;

        if (diff <= 0) return null;

        const totalSeconds = Math.floor(diff / 1000);

        return {
            hours: Math.floor(totalSeconds / 3600),
            minutes: Math.floor((totalSeconds % 3600) / 60),
            seconds: totalSeconds % 60,
        };
    };

    const cancelBooking = async (id) => {
        if (
            !window.confirm(
                "Are you sure you want to cancel this booking?"
            )
        ) {
            return;
        }

        try {
            setCancelLoading(id);

            await axios.post(
                `https://dubaihotelrates.com/api/booking/${id}/cancel`
            );

            alert("Booking cancelled");

            fetchBookings(user.id);
        } catch (err) {
            alert(
                err.response?.data?.message ||
                "Cancel failed"
            );
        } finally {
            setCancelLoading(null);
        }
    };

    /* =========================================================
       REVIEWS
    ========================================================= */

    const openReviewModal = (booking) => {
        setSelectedBooking(booking);
        setRating(0);
        setComment("");
        setShowReviewModal(true);
    };

    const submitReview = async () => {
        if (rating === 0) {
            alert("Please select a rating");
            return;
        }

        setLoading(true);

        try {
            if (selectedBooking.review) {
                await axios.put(
                    `https://dubaihotelrates.com/api/reviews/${selectedBooking.review.id}`,
                    {
                        rating,
                        comment,
                    }
                );

                alert("Review updated!");
            } else {
                await axios.post(
                    "https://dubaihotelrates.com/api/reviews",
                    {
                        booking_id: selectedBooking.id,
                        hotel_id: selectedBooking.hotel_id,
                        email: user.email,
                        rating,
                        comment,
                    }
                );

                alert("Review submitted!");
            }

            setShowReviewModal(false);
            setRating(0);
            setComment("");

            fetchBookings(user.id);
        } catch (err) {
            alert(
                err.response?.data?.message ||
                "Error"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteReview = async (reviewId) => {
        if (!window.confirm("Delete this review?")) {
            return;
        }

        try {
            await axios.delete(
                `https://dubaihotelrates.com/api/reviews/${reviewId}`
            );

            alert("Review deleted");

            fetchBookings(user.id);
        } catch (err) {
            alert("Delete failed");
        }
    };

    const handleEditReview = (booking) => {
        setSelectedBooking(booking);
        setRating(booking.review.rating);
        setComment(booking.review.comment);
        setShowReviewModal(true);
    };

    /* =========================================================
       LOGIN STATE
    ========================================================= */

    if (!user) {
        return (
            <div className="min-h-screen bg-[#F3F8FF] px-4 py-16 sm:px-6 lg:px-8 flex items-center justify-center">

                <div className="relative w-full max-w-md overflow-hidden rounded-[30px] border border-slate-200 bg-white p-8 text-center shadow-[0_20px_60px_rgba(15,23,42,0.08)] sm:p-10">

                    {/* Accent */}

                    <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-[#174EA6] to-[#FFC928]" />

                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EAF3FF] text-2xl text-[#174EA6]">
                        <FaLock />
                    </div>

                    <span className="mt-6 inline-flex items-center rounded-full bg-[#EAF3FF] px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#174EA6]">
                        My Account
                    </span>

                    <h2 className="mt-4 text-2xl font-extrabold text-[#0F172A] sm:text-3xl">
                        Please Login
                    </h2>

                    <p className="mt-3 text-sm leading-6 text-slate-500">
                        You must login to view your profile and bookings.
                    </p>

                    <button
                        onClick={onLoginClick}
                        className="group mt-7 inline-flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928] px-6 py-3.5 text-sm font-extrabold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
                    >
                        Go to Login

                        <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />
                    </button>

                </div>

            </div>
        );
    }

    /* =========================================================
       MAIN
    ========================================================= */

    return (
        <div className="min-h-screen bg-[#F3F8FF]">

            {/* =====================================================
            SMALL ACCOUNT HERO
        ===================================================== */}

            <section className="relative overflow-hidden bg-[#002B45]">

                {/* Decorative glow */}

                <div className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full bg-[#174EA6]/30 blur-3xl" />

                <div className="pointer-events-none absolute -left-32 bottom-[-120px] h-80 w-80 rounded-full bg-[#FFC928]/10 blur-3xl" />

                {/* Decorative circles */}

                <div className="pointer-events-none absolute right-[15%] top-1/2 h-32 w-32 -translate-y-1/2 rounded-full border border-white/5" />

                <div className="pointer-events-none absolute right-[17%] top-1/2 h-20 w-20 -translate-y-1/2 rounded-full border border-[#FFC928]/10" />


                <div className="relative z-10 mx-auto max-w-7xl px-4 pb-10 pt-28 sm:px-6 sm:pb-12 sm:pt-32 lg:px-8 lg:pb-14 lg:pt-36">

                    <div className="max-w-3xl">

                        {/* Badge */}

                        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#FFC928] backdrop-blur-sm">

                            <FaUserCircle className="text-xs" />

                            My Account

                        </div>


                        {/* Heading */}

                        <h1 className="mt-4 text-3xl font-extrabold tracking-tight !text-white sm:text-4xl lg:text-5xl">

                            Welcome,{" "}

                            <span className="text-[#FFC928]">
                                {user.name}
                            </span>

                        </h1>


                        {/* Description */}

                        <p className="mt-3 max-w-2xl text-sm leading-7 text-white/60 sm:text-base">

                            Manage your personal information, bookings,
                            cancellations and reviews all in one place.

                        </p>


                        {/* Small account status */}

                        <div className="mt-6 flex flex-wrap items-center gap-3">

                            <div className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-xs font-semibold text-white/80 backdrop-blur-sm">

                                <FaCheckCircle className="text-[#FFC928]" />

                                Account Active

                            </div>


                            <div className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-xs font-semibold text-white/80 backdrop-blur-sm">

                                <FaHotel className="text-[#FFC928]" />

                                {bookings.length}{" "}
                                {bookings.length === 1
                                    ? "Booking"
                                    : "Bookings"}

                            </div>

                        </div>

                    </div>

                </div>


                {/* Bottom accent */}

                <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928]" />

            </section>


            {/* =====================================================
            ACCOUNT CONTENT
        ===================================================== */}

            <div className="relative overflow-hidden px-4 py-10 sm:px-6 sm:py-12 lg:px-8">

                {/* Decorative Background */}

                <div className="pointer-events-none absolute -right-40 top-0 h-96 w-96 rounded-full bg-[#174EA6]/5 blur-3xl" />

                <div className="pointer-events-none absolute -left-40 bottom-0 h-96 w-96 rounded-full bg-[#FFC928]/5 blur-3xl" />


                <div className="relative z-10 mx-auto max-w-7xl">

                    {/* =====================================================
                    ACCOUNT CONTENT HEADER
                ===================================================== */}

                    <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">

                        <div>

                            <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#174EA6]">
                                Account Dashboard
                            </p>

                            <h2 className="mt-1 text-2xl font-extrabold text-[#0F172A] sm:text-3xl">
                                Manage Your Account
                            </h2>

                            <p className="mt-2 text-sm text-slate-500">
                                Update your details or manage your hotel reservations.
                            </p>

                        </div>


                        {/* Booking count */}

                        <div className="flex w-fit items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-3 shadow-[0_10px_35px_rgba(15,23,42,0.05)]">

                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EAF3FF] text-[#174EA6]">
                                <FaHotel />
                            </div>

                            <div>

                                <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                                    Total Bookings
                                </p>

                                <p className="text-xl font-extrabold text-[#0F172A]">
                                    {bookings.length}
                                </p>

                            </div>

                        </div>

                    </div>




                    {/* =====================================================
                    LAYOUT
                ===================================================== */}

                    <div className="grid gap-7 lg:grid-cols-[260px_1fr]">


                        {/* =================================================
                        SIDEBAR
                    ================================================= */}

                        <aside>

                            <div className="sticky top-24 overflow-hidden rounded-[26px] border border-slate-200 bg-white p-5 shadow-[0_15px_45px_rgba(15,23,42,0.06)]">

                                {/* User mini card */}

                                <div className="mb-5 rounded-2xl bg-[#F3F8FF] p-4">

                                    <div className="flex items-center gap-3">

                                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#174EA6] text-lg text-white">
                                            <FaUser />
                                        </div>

                                        <div className="min-w-0">

                                            <p className="truncate text-sm font-extrabold text-[#0F172A]">
                                                {user.name}
                                            </p>

                                            <p className="truncate text-xs text-slate-400">
                                                {user.email}
                                            </p>

                                        </div>

                                    </div>

                                </div>


                                <div className="mb-4">

                                    <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#174EA6]">
                                        Account
                                    </p>

                                    <h3 className="mt-1 text-lg font-extrabold text-[#0F172A]">
                                        My Account
                                    </h3>

                                </div>


                                {/* Profile */}

                                <button
                                    onClick={() => setActiveTab("profile")}
                                    className={`group mb-2 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-xs font-semibold transition-all duration-200 ${activeTab === "profile"
                                        ? "bg-[#EAF3FF] text-[#174EA6]"
                                        : "text-slate-500 hover:bg-[#F3F8FF] hover:text-[#174EA6]"
                                        }`}
                                >

                                    <span
                                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${activeTab === "profile"
                                            ? "bg-[#174EA6] text-white"
                                            : "bg-[#F3F8FF] text-[#174EA6]"
                                            }`}
                                    >
                                        <FaUser className="text-xs" />
                                    </span>

                                    <span className="flex-1">
                                        Profile
                                    </span>

                                    <FaArrowRight
                                        className={`text-[9px] transition-all ${activeTab === "profile"
                                            ? "translate-x-0 opacity-100"
                                            : "opacity-0 group-hover:translate-x-0.5 group-hover:opacity-100"
                                            }`}
                                    />

                                </button>


                                {/* Bookings */}

                                <button
                                    onClick={() => setActiveTab("bookings")}
                                    className={`group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-xs font-semibold transition-all duration-200 ${activeTab === "bookings"
                                        ? "bg-[#EAF3FF] text-[#174EA6]"
                                        : "text-slate-500 hover:bg-[#F3F8FF] hover:text-[#174EA6]"
                                        }`}
                                >

                                    <span
                                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${activeTab === "bookings"
                                            ? "bg-[#174EA6] text-white"
                                            : "bg-[#F3F8FF] text-[#174EA6]"
                                            }`}
                                    >
                                        <FaHotel className="text-xs" />
                                    </span>

                                    <span className="flex-1">
                                        My Bookings
                                    </span>

                                    <span
                                        className={`rounded-full px-2 py-0.5 text-[9px] font-extrabold ${activeTab === "bookings"
                                            ? "bg-[#FFC928] text-[#002B45]"
                                            : "bg-slate-100 text-slate-500"
                                            }`}
                                    >
                                        {bookings.length}
                                    </span>

                                </button>

                            </div>

                        </aside>


                        {/* =================================================
                        MAIN CONTENT
                    ================================================= */}

                        <main>


                            {/* =================================================
                            PROFILE TAB
                        ================================================= */}

                            {activeTab === "profile" && (

                                <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_15px_50px_rgba(15,23,42,0.06)]">

                                    {/* Top accent */}

                                    <div className="h-1 w-full bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928]" />

                                    <div className="p-6 sm:p-8 lg:p-10">

                                        {/* Header */}

                                        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">

                                            <div>

                                                <div className="flex items-center gap-3">

                                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EAF3FF] text-[#174EA6]">
                                                        <FaUser />
                                                    </div>

                                                    <div>

                                                        <h2 className="text-xl font-extrabold text-[#0F172A] sm:text-2xl">
                                                            Profile Information
                                                        </h2>

                                                        <p className="mt-1 text-xs text-slate-400">
                                                            Keep your account information up to date.
                                                        </p>

                                                    </div>

                                                </div>

                                            </div>

                                            <div className="hidden sm:flex items-center gap-2 rounded-full bg-[#EAF3FF] px-3 py-2 text-[10px] font-bold text-[#174EA6]">
                                                <FaCheckCircle />
                                                Account Active
                                            </div>

                                        </div>


                                        {/* Form */}

                                        <form
                                            onSubmit={updateProfile}
                                            className="grid grid-cols-1 gap-5 sm:grid-cols-2"
                                        >

                                            {/* Name */}

                                            <div>

                                                <label className="mb-2 block text-xs font-bold text-[#0F172A]">
                                                    Full Name
                                                </label>

                                                <div className="relative">

                                                    <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-[#174EA6]" />

                                                    <input
                                                        name="name"
                                                        value={form.name}
                                                        onChange={handleChange}
                                                        className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-[#0F172A] outline-none transition focus:border-[#174EA6] focus:ring-4 focus:ring-[#174EA6]/10"
                                                        placeholder="Full Name"
                                                    />

                                                </div>

                                            </div>


                                            {/* Email */}

                                            <div>

                                                <label className="mb-2 block text-xs font-bold text-[#0F172A]">
                                                    Email Address
                                                </label>

                                                <div className="relative">

                                                    <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-[#174EA6]" />

                                                    <input
                                                        name="email"
                                                        value={form.email}
                                                        onChange={handleChange}
                                                        className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-[#0F172A] outline-none transition focus:border-[#174EA6] focus:ring-4 focus:ring-[#174EA6]/10"
                                                        placeholder="Email Address"
                                                    />

                                                </div>

                                            </div>


                                            {/* Phone */}

                                            <div>

                                                <label className="mb-2 block text-xs font-bold text-[#0F172A]">
                                                    Phone Number
                                                </label>

                                                <div className="relative">

                                                    <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-[#174EA6]" />

                                                    <input
                                                        name="phone"
                                                        value={form.phone}
                                                        onChange={handleChange}
                                                        className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-[#0F172A] outline-none transition focus:border-[#174EA6] focus:ring-4 focus:ring-[#174EA6]/10"
                                                        placeholder="Phone Number"
                                                    />

                                                </div>

                                            </div>


                                            {/* Nationality */}

                                            <div>

                                                <label className="mb-2 block text-xs font-bold text-[#0F172A]">
                                                    Nationality
                                                </label>

                                                <div className="relative">

                                                    <FaGlobe className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-[#174EA6]" />

                                                    <input
                                                        name="nationality"
                                                        value={form.nationality}
                                                        onChange={handleChange}
                                                        className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-[#0F172A] outline-none transition focus:border-[#174EA6] focus:ring-4 focus:ring-[#174EA6]/10"
                                                        placeholder="Nationality"
                                                    />

                                                </div>

                                            </div>


                                            {/* Submit */}

                                            <div className="sm:col-span-2 pt-2">

                                                <button
                                                    type="submit"
                                                    className="group inline-flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928] px-6 py-3.5 text-sm font-extrabold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl sm:w-auto"
                                                >

                                                    Update Profile

                                                    <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />

                                                </button>

                                            </div>

                                        </form>

                                    </div>

                                </div>

                            )}


                            {/* =================================================
                            BOOKINGS TAB
                        ================================================= */}

                            {activeTab === "bookings" && (

                                <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_15px_50px_rgba(15,23,42,0.06)]">

                                    {/* Top Accent */}

                                    <div className="h-1 w-full bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928]" />


                                    <div className="p-6 sm:p-8 lg:p-10">

                                        {/* Header */}

                                        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">

                                            <div>

                                                <div className="flex items-center gap-3">

                                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EAF3FF] text-[#174EA6]">
                                                        <FaHotel />
                                                    </div>

                                                    <div>

                                                        <h2 className="text-2xl font-extrabold text-[#0F172A] sm:text-3xl">
                                                            My Bookings
                                                        </h2>

                                                        <p className="mt-1 text-sm text-slate-400">
                                                            View, manage cancellations and leave reviews.
                                                        </p>

                                                    </div>

                                                </div>

                                            </div>


                                            <div className="flex items-center gap-3 rounded-2xl bg-[#F3F8FF] px-5 py-3">

                                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#174EA6] text-white">
                                                    <FaCalendarAlt className="text-sm" />
                                                </div>

                                                <div>

                                                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                                                        Total Bookings
                                                    </p>

                                                    <p className="text-lg font-extrabold text-[#0F172A]">
                                                        {bookings.length}
                                                    </p>

                                                </div>

                                            </div>

                                        </div>


                                        {/* Empty */}

                                        {bookings.length === 0 ? (

                                            <div className="rounded-[24px] border border-slate-100 bg-[#F8FBFF] py-20 text-center">

                                                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EAF3FF] text-2xl text-[#174EA6]">
                                                    <FaHotel />
                                                </div>

                                                <h3 className="mt-5 text-lg font-extrabold text-[#0F172A]">
                                                    No bookings yet
                                                </h3>

                                                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-400">
                                                    Your upcoming reservations will appear here once you make a booking.
                                                </p>

                                            </div>

                                        ) : (

                                            <div className="space-y-6">

                                                {bookings.map((b) => (

                                                    <div
                                                        key={b.id}
                                                        className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-[0_10px_35px_rgba(15,23,42,0.04)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_15px_45px_rgba(15,23,42,0.08)]"
                                                    >

                                                        {/* =================================================
                                                        BOOKING HEADER
                                                    ================================================= */}

                                                        <div className="border-b border-slate-100 bg-[#F8FBFF] px-5 py-5 sm:px-6">

                                                            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">

                                                                <div className="flex items-start gap-4">

                                                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#174EA6] text-white">
                                                                        <FaHotel />
                                                                    </div>

                                                                    <div>

                                                                        <p className="mb-1 text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#174EA6]">
                                                                            Reservation
                                                                        </p>

                                                                        <h3 className="text-lg font-extrabold text-[#0F172A] sm:text-xl">
                                                                            {b.hotel_name}
                                                                        </h3>

                                                                        <p className="mt-1 text-xs text-slate-400 sm:text-sm">
                                                                            {b.room_name}{" "}
                                                                            <span className="mx-1">
                                                                                •
                                                                            </span>
                                                                            Room {b.room_number}
                                                                        </p>

                                                                    </div>

                                                                </div>


                                                                {/* Status */}

                                                                <div>

                                                                    <span
                                                                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[10px] font-extrabold uppercase tracking-wider ${b.status === "confirmed"
                                                                            ? "bg-[#EAF3FF] text-[#174EA6]"
                                                                            : b.status === "cancelled"
                                                                                ? "bg-red-50 text-red-600"
                                                                                : "bg-yellow-50 text-yellow-700"
                                                                            }`}
                                                                    >

                                                                        {b.status === "confirmed" ? (
                                                                            <FaCheckCircle />
                                                                        ) : b.status === "cancelled" ? (
                                                                            <FaTimesCircle />
                                                                        ) : (
                                                                            <FaClock />
                                                                        )}

                                                                        {b.status}

                                                                    </span>

                                                                </div>

                                                            </div>

                                                        </div>


                                                        {/* =================================================
                                                        BOOKING CONTENT
                                                    ================================================= */}

                                                        <div className="p-5 sm:p-6">


                                                            {/* Information */}

                                                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">

                                                                {/* Check in */}

                                                                <div className="rounded-2xl bg-[#F8FBFF] p-4">

                                                                    <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-[#EAF3FF] text-[#174EA6]">
                                                                        <FaCalendarAlt className="text-xs" />
                                                                    </div>

                                                                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                                                        Check-in
                                                                    </p>

                                                                    <p className="mt-1 text-sm font-extrabold text-[#0F172A]">
                                                                        {formatDate(b.check_in)}
                                                                    </p>

                                                                </div>


                                                                {/* Check out */}

                                                                <div className="rounded-2xl bg-[#F8FBFF] p-4">

                                                                    <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-[#EAF3FF] text-[#174EA6]">
                                                                        <FaCalendarAlt className="text-xs" />
                                                                    </div>

                                                                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                                                        Check-out
                                                                    </p>

                                                                    <p className="mt-1 text-sm font-extrabold text-[#0F172A]">
                                                                        {formatDate(b.check_out)}
                                                                    </p>

                                                                </div>


                                                                {/* Guests */}

                                                                <div className="rounded-2xl bg-[#F8FBFF] p-4">

                                                                    <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-[#EAF3FF] text-[#174EA6]">
                                                                        <FaUser className="text-xs" />
                                                                    </div>

                                                                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                                                        Guests
                                                                    </p>

                                                                    <p className="mt-1 text-sm font-extrabold text-[#0F172A]">
                                                                        {b.guests} Guests
                                                                    </p>

                                                                </div>


                                                                {/* Amount */}

                                                                <div className="rounded-2xl bg-[#FFF9E5] p-4">

                                                                    <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-[#FFC928] text-[#002B45]">
                                                                        <span className="text-xs font-extrabold">
                                                                            AED
                                                                        </span>
                                                                    </div>

                                                                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#947300]">
                                                                        Amount Paid
                                                                    </p>

                                                                    <p className="mt-1 text-sm font-extrabold text-[#002B45]">
                                                                        AED {b.total_amount}
                                                                    </p>

                                                                </div>

                                                            </div>


                                                            {/* =================================================
                                                            ACTIONS
                                                        ================================================= */}

                                                            <div className="mt-6 flex flex-wrap items-center gap-3">


                                                                {/* Cancel */}

                                                                {b.status === "confirmed" &&
                                                                    canCancel(b) && (
                                                                        <>

                                                                            <button
                                                                                onClick={() =>
                                                                                    cancelBooking(b.id)
                                                                                }
                                                                                disabled={
                                                                                    cancelLoading ===
                                                                                    b.id
                                                                                }
                                                                                className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-xs font-extrabold transition ${cancelLoading ===
                                                                                    b.id
                                                                                    ? "cursor-not-allowed bg-red-300 text-white"
                                                                                    : "bg-red-50 text-red-600 hover:bg-red-100"
                                                                                    }`}
                                                                            >

                                                                                {cancelLoading ===
                                                                                    b.id ? (
                                                                                    <>
                                                                                        <svg
                                                                                            className="h-4 w-4 animate-spin"
                                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                                            fill="none"
                                                                                            viewBox="0 0 24 24"
                                                                                        >
                                                                                            <circle
                                                                                                className="opacity-25"
                                                                                                cx="12"
                                                                                                cy="12"
                                                                                                r="10"
                                                                                                stroke="currentColor"
                                                                                                strokeWidth="4"
                                                                                            />

                                                                                            <path
                                                                                                className="opacity-75"
                                                                                                fill="currentColor"
                                                                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                                                            />
                                                                                        </svg>

                                                                                        Cancelling...
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <FaTimesCircle />
                                                                                        Cancel Booking
                                                                                    </>
                                                                                )}

                                                                            </button>


                                                                            {/* Countdown */}

                                                                            {(() => {

                                                                                const time =
                                                                                    getCancelCountdown(
                                                                                        b
                                                                                    );

                                                                                if (!time)
                                                                                    return null;

                                                                                return (
                                                                                    <div className="inline-flex items-center gap-2 rounded-xl border border-[#FFE8A0] bg-[#FFF9E5] px-4 py-2.5 text-[10px] font-bold text-[#947300]">

                                                                                        <FaClock />

                                                                                        Free cancellation ends in{" "}

                                                                                        <span className="font-extrabold">
                                                                                            {time.hours
                                                                                                .toString()
                                                                                                .padStart(
                                                                                                    2,
                                                                                                    "0"
                                                                                                )}
                                                                                            :
                                                                                            {time.minutes
                                                                                                .toString()
                                                                                                .padStart(
                                                                                                    2,
                                                                                                    "0"
                                                                                                )}
                                                                                            :
                                                                                            {time.seconds
                                                                                                .toString()
                                                                                                .padStart(
                                                                                                    2,
                                                                                                    "0"
                                                                                                )}
                                                                                        </span>

                                                                                    </div>
                                                                                );

                                                                            })()}

                                                                        </>
                                                                    )}


                                                                {/* Leave Review */}

                                                                {b.status === "confirmed" &&
                                                                    isPastCheckout(
                                                                        b.check_out
                                                                    ) &&
                                                                    !b.review && (

                                                                        <button
                                                                            onClick={() =>
                                                                                openReviewModal(
                                                                                    b
                                                                                )
                                                                            }
                                                                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#FFC928] px-5 py-2.5 text-xs font-extrabold text-[#002B45] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                                                                        >
                                                                            <FaStar />
                                                                            Leave Review
                                                                        </button>

                                                                    )}

                                                            </div>


                                                            {/* Review availability */}

                                                            {b.status === "confirmed" &&
                                                                !isPastCheckout(
                                                                    b.check_out
                                                                ) && (

                                                                    <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">

                                                                        <FaClock className="text-[#174EA6]" />

                                                                        Reviews can be submitted after checkout.

                                                                    </div>

                                                                )}


                                                            {/* =================================================
                                                            REVIEW
                                                        ================================================= */}

                                                            {b.review && (

                                                                <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-[#F8FBFF]">

                                                                    <div className="border-b border-slate-100 px-5 py-4">

                                                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

                                                                            <div className="flex items-center gap-3">

                                                                                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#EAF3FF] text-[#174EA6]">
                                                                                    <FaStar className="text-xs" />
                                                                                </div>

                                                                                <h4 className="text-sm font-extrabold text-[#0F172A]">
                                                                                    Your Review
                                                                                </h4>

                                                                            </div>


                                                                            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#FFF9E5] px-3 py-1.5 text-xs font-extrabold text-[#947300]">
                                                                                <FaStar />
                                                                                {b.review.rating}/5
                                                                            </span>

                                                                        </div>

                                                                    </div>


                                                                    <div className="p-5">

                                                                        <p className="text-sm leading-7 text-slate-500">
                                                                            {b.review.comment}
                                                                        </p>


                                                                        <div className="mt-5 flex flex-wrap gap-3">

                                                                            <button
                                                                                onClick={() =>
                                                                                    handleEditReview(
                                                                                        b
                                                                                    )
                                                                                }
                                                                                className="inline-flex items-center gap-2 rounded-xl bg-[#EAF3FF] px-4 py-2 text-xs font-extrabold text-[#174EA6] transition hover:bg-[#DDEEFF]"
                                                                            >
                                                                                <FaEdit />
                                                                                Edit Review
                                                                            </button>


                                                                            <button
                                                                                onClick={() =>
                                                                                    handleDeleteReview(
                                                                                        b.review.id
                                                                                    )
                                                                                }
                                                                                className="inline-flex items-center gap-2 rounded-xl bg-red-50 px-4 py-2 text-xs font-extrabold text-red-600 transition hover:bg-red-100"
                                                                            >
                                                                                <FaTrash />
                                                                                Delete Review
                                                                            </button>

                                                                        </div>

                                                                    </div>

                                                                </div>

                                                            )}

                                                        </div>

                                                    </div>

                                                ))}

                                            </div>

                                        )}

                                    </div>

                                </div>

                            )}

                        </main>

                    </div>

                </div>

            </div>




            {
                showReviewModal && (

                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#002B45]/70 px-4 backdrop-blur-sm">

                        <div className="relative w-full max-w-md overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_25px_80px_rgba(0,43,69,0.25)]">

                            {/* Accent */}

                            <div className="h-1 w-full bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928]" />


                            <div className="p-6 sm:p-8">

                                {/* Header */}

                                <div className="flex items-start justify-between gap-4">

                                    <div>

                                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EAF3FF] text-[#174EA6]">
                                            <FaStar />
                                        </div>

                                        <h2 className="mt-4 text-xl font-extrabold text-[#0F172A] sm:text-2xl">
                                            {selectedBooking?.review
                                                ? "Edit Review"
                                                : "Add Review"}
                                        </h2>

                                        <p className="mt-1 text-xs text-slate-400">
                                            {selectedBooking?.hotel_name}
                                        </p>

                                    </div>


                                    <button
                                        onClick={() =>
                                            setShowReviewModal(false)
                                        }
                                        className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-50 text-slate-400 transition hover:bg-red-50 hover:text-red-500"
                                    >
                                        <FaTimesCircle />
                                    </button>

                                </div>


                                {/* Rating */}

                                <div className="mt-7">

                                    <p className="mb-3 text-xs font-extrabold text-[#0F172A]">
                                        Your Rating
                                    </p>

                                    <div className="flex items-center gap-2">

                                        {[1, 2, 3, 4, 5].map(
                                            (star) => (

                                                <button
                                                    key={star}
                                                    type="button"
                                                    onClick={() =>
                                                        setRating(star)
                                                    }
                                                    className={`flex h-10 w-10 items-center justify-center rounded-xl transition-all ${star <= rating
                                                        ? "bg-[#FFF9E5] text-[#FFC928] scale-105"
                                                        : "bg-slate-50 text-slate-300 hover:bg-[#FFF9E5] hover:text-[#FFC928]"
                                                        }`}
                                                >
                                                    <FaStar />
                                                </button>

                                            )
                                        )}

                                    </div>

                                </div>


                                {/* Comment */}

                                <div className="mt-6">

                                    <label className="mb-2 block text-xs font-extrabold text-[#0F172A]">
                                        Your Experience
                                    </label>

                                    <textarea
                                        value={comment}
                                        onChange={(e) =>
                                            setComment(
                                                e.target.value
                                            )
                                        }
                                        placeholder="Write your experience..."
                                        className="w-full resize-none rounded-xl border border-slate-200 bg-white p-4 text-sm text-[#0F172A] outline-none transition placeholder:text-slate-400 focus:border-[#174EA6] focus:ring-4 focus:ring-[#174EA6]/10"
                                        rows="4"
                                    />

                                </div>


                                {/* Submit */}

                                <button
                                    onClick={submitReview}
                                    disabled={loading}
                                    className={`group mt-6 inline-flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928] px-6 py-3.5 text-sm font-extrabold text-white shadow-lg transition-all duration-300 ${loading
                                        ? "cursor-not-allowed opacity-70"
                                        : "hover:-translate-y-0.5 hover:shadow-xl"
                                        }`}
                                >

                                    {loading
                                        ? "Submitting..."
                                        : selectedBooking?.review
                                            ? "Update Review"
                                            : "Submit Review"}

                                    {!loading && (
                                        <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />
                                    )}

                                </button>


                                {/* Close */}

                                <button
                                    onClick={() =>
                                        setShowReviewModal(false)
                                    }
                                    className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-5 py-3 text-xs font-bold text-slate-500 transition hover:bg-slate-50"
                                >
                                    Close
                                </button>

                            </div>

                        </div>

                    </div>

                )
            }

        </div >
    );
}